import styled, { keyframes } from 'styled-components';
import colors from '../../../styles/colors';
import { DropdownContainer } from '../shared/DeviceDropdownStyle';

const pulse = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

export const WaitingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  background: rgba(10, 10, 15, 0.85);
  z-index: 1;

  svg {
    width: 72px;
    height: 72px;
    opacity: 0.5;
    position: static;
    transform: none;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.75);
    font-size: 15px;
    font-weight: 500;
    letter-spacing: 0.02em;
  }
`;

export const WaitingDots = styled.div`
  display: flex;
  gap: 8px;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    display: inline-block;
    animation: ${pulse} 1.4s ease-in-out infinite;

    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

export const InCallWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`;

export const Center = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;
  align-items: center;
`;

export const Content = styled.div`
  background: black;
  flex: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const BlurButton = styled.button<{ $isVideoBlurred?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    border-radius: 1200px;
    background: transparent;

    &:focus-visible {
        outline: 2px solid #005fcc;
        outline-offset: 2px;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }

    &:active {
        background: rgba(255, 255, 255, 0.3);
    }
    ${({ $isVideoBlurred }) => $isVideoBlurred && 'background: rgba(255, 255, 255, 0.2);'}}
`;

export const SettingsButton = styled.div`
  position: relative;
  button {
  }
`;

export const SettingsModalTitle = styled.span`
  margin-right: auto;
  color: ${colors.blue['950']};
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 8px;
`;

export const SettingsModal = styled.div`
  position: absolute;
  background: #ffffff;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: 16px;
  bottom: 65px;
  right: 0;
  width: 280px;
  z-index: 9999;
  left: 50%;
  gap: 16px;
  transform: translate(-50%, 0);

  ${DropdownContainer} {
    max-width: none;
  }
`;

export const DialPadModal = styled.div`
  position: absolute;
  background: #ffffff;
  padding: 20px;
  flex-direction: column;
  border-radius: 16px;
  bottom: 65px;
  right: 0;
  width: 240px;
  z-index: 9999;
  left: 50%;
  transform: translate(-50%, 0);
  display: grid;
  grid-template-columns: repeat(3, 60px);
  grid-template-rows: repeat(5, 60px);
  gap: 16px;
  justify-content: center;

  div {
    height: 100%;
    grid-column: span 3;
    display: flex;
    align-items: center;
    background: transparent;

    input {
      outline: none;
      border: none;
      font-size: 26px;
      text-align: center;
      width: 100%;
    }

    &:hover {
      background: transparent;
    }
  }

  button {
    background-color: #f1eff7;
    border-radius: 80px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 26px;
    font-weight: 700;
    cursor: pointer;
    color: ${colors.blue['950']};
    transition: background-color ease-in-out 0.1s;

    &:focus-visible {
      outline: 2px solid #005fcc;
      outline-offset: 2px;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    &:active {
      background: rgba(255, 255, 255, 0.3);
    }

    &:hover {
      background-color: #e7e4ec;
    }
  }
`;

export const RotateCameraButton = styled.button`
  display: none;
`;

export const Media = styled.div`
  flex: 1;
  position: relative;
  margin: 8px;

  ${BlurButton} {
    display: none;
  }

  @media (max-width: 640px) {
    ${BlurButton} {
      position: absolute;
      left: 24px;
      bottom: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 54px;
      height: 54px;
      outline: none;
      border: none;
      border-radius: 100px;
      transition: 0.15s all ease-in-out;
      background: transparent;

      &:hover {
        background: rgba(0, 0, 0, 0.52);
      }
    }

    ${RotateCameraButton} {
      position: absolute;
      right: 24px;
      top: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 54px;
      height: 54px;
      background: rgba(0, 0, 0, 0.32);
      outline: none;
      border: none;
      border-radius: 100px;
      transition: 0.15s all ease-in-out;
      cursor: pointer;

      &:hover {
        background: rgba(0, 0, 0, 0.52);
      }
    }
  }
`;

export const ToggleButton = styled.button<{ $muted: boolean }>`
  background: ${({ $muted }) => ($muted ? 'rgba(255, 255, 255, 0.2)' : 'transparent')};
  border-radius: 1200px;

  &:focus-visible {
    outline: 2px solid #005fcc;
    outline-offset: 2px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    background: rgba(255, 255, 255, 0.3);
  }
`;

export const Controls = styled.div`
  display: flex;
  align-items: center;
  margin-top: auto;
  height: 94px;
  background: rgba(10, 10, 15, 0.54);
  border-radius: 12px;

  button {
    width: 48px;
    height: 48px;
    border: none;
    outline: none;
    cursor: pointer;

    svg {
      path {
        transition: all 0.1s ease-in-out;
      }
    }

    &:hover {
      svg {
        path {
          fill: ${colors.grey['200']};
        }
      }
    }
  }
`;

export const Left = styled.div`
  display: flex;
  flex: 1;
`;

export const Disconnect = styled.button`
  &:focus-visible {
    outline: 2px solid ${colors.red['500']};
    outline-offset: 2px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    background: rgba(255, 255, 255, 0.3);
  }
`;

export const Middle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  gap: 12px;
  position: relative;

  ${Disconnect} {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 54px;
    width: 54px;
    background: ${colors.red['500']};
    border-radius: 54px;
    transition: all 0.1s ease-in-out;

    svg {
      rotate: 135deg;
      path {
        fill: #ffffff;
      }
    }

    &:hover {
      background: ${colors.red['600']};
    }
  }
`;

export const Right = styled.div<{ $isVideoBlurred: boolean }>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex: 1;

  @media (max-width: 640px) {
    ${BlurButton} {
      display: none;
    }
  }
`;

export const PoweredBy = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-size: 12px;
  height: 33px;
  color: #fff;
  border-top: 1px solid rgba(83, 87, 117, 1);
  background: rgba(10, 10, 15, 0.54);

  a {
    text-decoration: none;
    color: #fff;
  }

  > div {
    position: absolute;
    right: 16px;
  }
`;

export const RemoteVideo = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;
  align-items: center;
  object-fit: cover;
  background: black;
  border-radius: 8px;
  background: rgba(10, 10, 15, 0.44);
  position: relative;
  overflow: hidden;

  video {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 16px;
  }

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

export const LocalVideo = styled.div<{ $isVideoBlurred: boolean }>`
  width: 460px;
  height: 240px;
  align-items: center;
  position: absolute;
  right: 24px;
  bottom: 24px;
  border-radius: 8px;
  background: rgba(10, 10, 15, 1);
  overflow: hidden;

  @media (max-width: 640px) {
    left: 24px;
    top: 24px;
    bottom: unset;
    right: unset;
    width: 75px;
    height: 120px;

    svg {
      width: 37px;
      height: 37px;
    }
  }

  /* Vonage video elements */
  .OT_root {
    width: 100% !important;
    height: 100% !important;
    position: absolute;
    top: 0;
    left: 0;
  }

  .OT_video-element {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  div {
    width: 100%;
    height: 100%;
    position: relative;

    video,
    canvas {
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    canvas {
      ${({ $isVideoBlurred }) => !$isVideoBlurred && 'opacity:0'}
    }

    svg {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
`;
